// Word (.docx) export of meetings and inspection reports.
// The `docx` package is large, so it is imported dynamically inside the generators: Vite splits it into
// its own chunk that is only fetched (or read from the service-worker cache) when the user exports.
import type { Paragraph, Table, TextRun } from 'docx';
import type { DocumentFull } from '../../../shared/types';
import { formatOffset } from '../lib/format';
import {
  clipBody,
  inspectionEntries,
  inspectionHeaderRows,
  meetingDateLine,
  partHeading,
  participantNames,
  placeholderFor,
  sortedClips,
  textParagraphs,
  type ClipBody,
  type SpeakerTurn,
} from './common';

type Docx = typeof import('docx');
type Block = Paragraph | Table;

export const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const FONT = 'Calibri';
const LANGUAGE = 'sl-SI';
/** Run sizes are in half-points. */
const SIZE_BODY = 22; // 11 pt
const SIZE_SMALL = 18; // 9 pt
const COLOR_TEXT = '1F2937';
const COLOR_MUTED = '6B7280';
const COLOR_BORDER = 'BFC5CC';
const COLOR_SHADE = 'F1F3F5';

/** A4 portrait in twips (1/20 pt) with 2 cm margins. */
const PAGE_WIDTH = 11906;
const PAGE_HEIGHT = 16838;
const MARGIN = 1134;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;

const NUMBERING_DECISIONS = 'baam-sklepi';
const NUMBERING_FINDINGS = 'baam-ugotovitve';
const LIST_INDENT = 567; // 1 cm

async function loadDocx(): Promise<Docx> {
  try {
    return await import('docx');
  } catch (err) {
    // Typically an offline device whose service worker has not cached the export chunk yet.
    throw new Error('Modula za izvoz v Word ni bilo mogoče naložiti. Preverite povezavo in poskusite znova.', {
      cause: err,
    });
  }
}

/**
 * Characters that are not allowed in XML 1.0 make Word refuse to open the whole file, and transcripts
 * can contain them (pasted text, provider glitches). Lone surrogates are invalid too.
 */
function xmlSafe(text: string): string {
  return (
    text
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g, '')
      .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, '')
  );
}

/** Builder helpers bound to the lazily loaded module. */
function createKit(docx: Docx) {
  const run = (text: string, opts: { bold?: boolean; italics?: boolean; color?: string; size?: number } = {}): TextRun =>
    new docx.TextRun({ text: xmlSafe(text), ...opts });

  /** Muted italic placeholder such as "[prepis v teku]". */
  const placeholder = (text: string): TextRun => run(text, { italics: true, color: COLOR_MUTED });

  const heading = (text: string, level: 1 | 2 | 3, opts: { center?: boolean } = {}): Paragraph =>
    new docx.Paragraph({
      heading: level === 1 ? docx.HeadingLevel.HEADING_1 : level === 2 ? docx.HeadingLevel.HEADING_2 : docx.HeadingLevel.HEADING_3,
      alignment: opts.center ? docx.AlignmentType.CENTER : undefined,
      children: [run(text)],
    });

  const paragraph = (children: TextRun[], opts: { indent?: number; keepNext?: boolean; spacingAfter?: number } = {}): Paragraph =>
    new docx.Paragraph({
      children,
      keepNext: opts.keepNext,
      indent: opts.indent ? { left: opts.indent } : undefined,
      spacing: opts.spacingAfter !== undefined ? { after: opts.spacingAfter } : undefined,
    });

  const border = { style: docx.BorderStyle.SINGLE, size: 4, color: COLOR_BORDER } as const;
  const tableBorders = {
    top: border,
    bottom: border,
    left: border,
    right: border,
    insideHorizontal: border,
    insideVertical: border,
  };
  const cellMargins = { top: 60, bottom: 60, left: 100, right: 100 };

  const cell = (lines: string[], width: number, opts: { header?: boolean; shade?: boolean } = {}) =>
    new docx.TableCell({
      width: { size: width, type: docx.WidthType.DXA },
      margins: cellMargins,
      verticalAlign: docx.VerticalAlignTable.TOP,
      shading: opts.header || opts.shade ? { type: docx.ShadingType.CLEAR, color: 'auto', fill: COLOR_SHADE } : undefined,
      children: (lines.length > 0 ? lines : ['']).map(
        (line) =>
          new docx.Paragraph({
            spacing: { after: 0 },
            children: [run(line, { bold: opts.header || opts.shade })],
          }),
      ),
    });

  /** Full-width table with fixed column widths (Word and LibreOffice both honour tblGrid + fixed layout). */
  const table = (columnWidths: number[], rows: Array<{ cells: string[][]; header?: boolean; shadeFirst?: boolean }>): Table =>
    new docx.Table({
      width: { size: CONTENT_WIDTH, type: docx.WidthType.DXA },
      columnWidths,
      layout: docx.TableLayoutType.FIXED,
      borders: tableBorders,
      rows: rows.map(
        (row) =>
          new docx.TableRow({
            tableHeader: row.header,
            cantSplit: true,
            children: row.cells.map((lines, i) =>
              cell(lines, columnWidths[i] ?? 0, { header: row.header, shade: row.shadeFirst && i === 0 }),
            ),
          }),
      ),
    });

  const footer = () =>
    new docx.Footer({
      children: [
        new docx.Paragraph({
          alignment: docx.AlignmentType.CENTER,
          spacing: { after: 0 },
          children: [
            new docx.TextRun({
              size: SIZE_SMALL,
              color: COLOR_MUTED,
              children: ['Stran ', docx.PageNumber.CURRENT, ' od ', docx.PageNumber.TOTAL_PAGES],
            }),
          ],
        }),
      ],
    });

  const document = (opts: { title: string; description: string; children: Block[] }) =>
    new docx.Document({
      creator: 'BAAM Transkripcija',
      lastModifiedBy: 'BAAM Transkripcija',
      title: xmlSafe(opts.title),
      description: opts.description,
      styles: {
        default: {
          document: {
            run: { font: FONT, size: SIZE_BODY, color: COLOR_TEXT, language: { value: LANGUAGE } },
            paragraph: { spacing: { after: 120, line: 276 } },
          },
          heading1: {
            run: { font: FONT, size: 32, bold: true, color: COLOR_TEXT },
            paragraph: { spacing: { before: 0, after: 120 }, keepNext: true },
          },
          heading2: {
            run: { font: FONT, size: 26, bold: true, color: COLOR_TEXT },
            paragraph: { spacing: { before: 320, after: 120 }, keepNext: true },
          },
          heading3: {
            run: { font: FONT, size: 23, bold: true, color: COLOR_MUTED },
            paragraph: { spacing: { before: 240, after: 80 }, keepNext: true },
          },
        },
      },
      numbering: {
        config: [
          {
            reference: NUMBERING_DECISIONS,
            levels: [
              {
                level: 0,
                format: docx.LevelFormat.DECIMAL,
                text: '%1.',
                alignment: docx.AlignmentType.LEFT,
                style: { paragraph: { indent: { left: LIST_INDENT, hanging: 397 } } },
              },
            ],
          },
          {
            reference: NUMBERING_FINDINGS,
            levels: [
              {
                level: 0,
                format: docx.LevelFormat.DECIMAL,
                text: '%1.',
                alignment: docx.AlignmentType.LEFT,
                style: {
                  run: { bold: true },
                  paragraph: { indent: { left: LIST_INDENT, hanging: LIST_INDENT } },
                },
              },
            ],
          },
        ],
      },
      sections: [
        {
          properties: {
            page: {
              size: { width: PAGE_WIDTH, height: PAGE_HEIGHT, orientation: docx.PageOrientation.PORTRAIT },
              margin: { top: MARGIN, right: MARGIN, bottom: MARGIN, left: MARGIN, header: 567, footer: 567 },
            },
          },
          footers: { default: footer() },
          children: opts.children,
        },
      ],
    });

  return { run, placeholder, heading, paragraph, table, document };
}

type Kit = ReturnType<typeof createKit>;

async function pack(docx: Docx, document: InstanceType<Docx['Document']>): Promise<Blob> {
  // Wrapping the bytes ourselves guarantees the Word MIME type (used for sharing) and behaves the same in
  // browsers and in jsdom-based tests.
  const buffer = await docx.Packer.toArrayBuffer(document);
  return new Blob([buffer], { type: DOCX_MIME });
}

// ---------- Meeting ----------

function turnParagraph(kit: Kit, turn: SpeakerTurn): Paragraph {
  const children: TextRun[] = [];
  if (turn.name) children.push(kit.run(turn.name, { bold: true }), kit.run(' '));
  children.push(
    kit.run(`[${formatOffset(turn.startMs)}]`, { color: COLOR_MUTED, size: SIZE_SMALL }),
    kit.run(' '),
    kit.run(turn.text),
  );
  return kit.paragraph(children);
}

function meetingClipBlocks(kit: Kit, body: ClipBody): Paragraph[] {
  const note = 'note' in body && body.note ? [kit.paragraph([kit.placeholder(body.note)])] : [];
  switch (body.kind) {
    case 'turns':
      return [...note, ...body.turns.map((turn) => turnParagraph(kit, turn))];
    case 'paragraphs':
      return [...note, ...body.paragraphs.map((text) => kit.paragraph([kit.run(text)]))];
    default:
      return [kit.paragraph([kit.placeholder(placeholderFor(body) ?? '')])];
  }
}

function minutesBlocks(docx: Docx, kit: Kit, doc: DocumentFull): Block[] {
  const minutes = doc.minutes;
  if (!minutes) return [];
  const blocks: Block[] = [];

  const summary = textParagraphs(minutes.povzetek);
  if (summary.length > 0) {
    blocks.push(kit.heading('Povzetek', 2), ...summary.map((text) => kit.paragraph([kit.run(text)])));
  }

  const topics = minutes.teme.map((t) => t.trim()).filter(Boolean);
  if (topics.length > 0) {
    blocks.push(
      kit.heading('Teme', 2),
      ...topics.map((text) => new docx.Paragraph({ bullet: { level: 0 }, spacing: { after: 60 }, children: [kit.run(text)] })),
    );
  }

  const decisions = minutes.sklepi.map((s) => s.trim()).filter(Boolean);
  if (decisions.length > 0) {
    blocks.push(
      kit.heading('Sklepi', 2),
      ...decisions.map(
        (text) =>
          new docx.Paragraph({
            numbering: { reference: NUMBERING_DECISIONS, level: 0 },
            spacing: { after: 60 },
            children: [kit.run(text)],
          }),
      ),
    );
  }

  const tasks = minutes.naloge.filter((n) => n.opis.trim());
  if (tasks.length > 0) {
    const widths = [5338, 2400, CONTENT_WIDTH - 5338 - 2400];
    blocks.push(
      kit.heading('Naloge', 2),
      kit.table(widths, [
        { header: true, cells: [['Naloga'], ['Nosilec'], ['Rok']] },
        ...tasks.map((n) => ({
          cells: [textParagraphs(n.opis), [n.nosilec?.trim() || '—'], [n.rok?.trim() || '—']],
        })),
      ]),
    );
  }
  return blocks;
}

/** Word document: title, date, duration, participants (speaker names), optional AI minutes, transcript by speaker with timestamps. */
export async function meetingToDocx(doc: DocumentFull): Promise<Blob> {
  const docx = await loadDocx();
  const kit = createKit(docx);
  const children: Block[] = [kit.heading(doc.title, 1), kit.paragraph([kit.run(meetingDateLine(doc), { color: COLOR_MUTED })])];

  const names = participantNames(doc);
  if (names.length > 0) {
    children.push(kit.paragraph([kit.run('Udeleženci: ', { bold: true }), kit.run(names.join(', '))]));
  }

  children.push(...minutesBlocks(docx, kit, doc));

  children.push(kit.heading('Prepis', 2));
  const clips = sortedClips(doc);
  if (clips.length === 0) children.push(kit.paragraph([kit.placeholder('Ni posnetkov.')]));
  clips.forEach((clip, index) => {
    if (clips.length > 1) children.push(kit.heading(partHeading(index, clip), 3));
    children.push(...meetingClipBlocks(kit, clipBody(doc, clip, { turns: true })));
  });

  return pack(docx, kit.document({ title: doc.title, description: 'Zapisnik sestanka', children }));
}

// ---------- Inspection ----------

const SIGNATURE_BLANK = '________________________';

function signatureLine(docx: Docx, kit: Kit, label: string, opts: { value?: string; last?: boolean } = {}): Paragraph {
  return new docx.Paragraph({
    // keepNext chains the lines so the block is never split across two pages.
    keepNext: !opts.last,
    keepLines: true,
    spacing: { before: 240, after: 0 },
    children: [kit.run(`${label}: `, { bold: true }), kit.run(opts.value || SIGNATURE_BLANK)],
  });
}

/** Word document "Zapisnik nadzora": header table from doc.meta, numbered entries with time, signature line. */
export async function inspectionToDocx(doc: DocumentFull): Promise<Blob> {
  const docx = await loadDocx();
  const kit = createKit(docx);
  const children: Block[] = [
    kit.heading('ZAPISNIK NADZORA', 1, { center: true }),
    new docx.Paragraph({
      alignment: docx.AlignmentType.CENTER,
      spacing: { after: 240 },
      children: [kit.run(doc.title, { size: 26, color: COLOR_MUTED })],
    }),
  ];

  const labelWidth = 2552; // 4.5 cm
  children.push(
    kit.table(
      [labelWidth, CONTENT_WIDTH - labelWidth],
      inspectionHeaderRows(doc).map((row) => ({ shadeFirst: true, cells: [[row.label], row.lines] })),
    ),
  );

  children.push(kit.heading('Ugotovitve', 2));
  const entries = inspectionEntries(doc);
  if (entries.length === 0) children.push(kit.paragraph([kit.placeholder('Ni vnosov.')]));
  for (const entry of entries) {
    const time = entry.time ? [kit.run(entry.time, { color: COLOR_MUTED, size: SIZE_SMALL }), kit.run('  ')] : [];
    if (entry.body.kind === 'paragraphs') {
      const [first = '', ...rest] = entry.body.paragraphs;
      // A status note (re-transcription in progress) follows the entry's last line.
      const note = entry.body.note ? [kit.run(' '), kit.placeholder(entry.body.note)] : [];
      children.push(
        new docx.Paragraph({
          numbering: { reference: NUMBERING_FINDINGS, level: 0 },
          keepNext: rest.length > 0,
          spacing: { after: rest.length > 0 ? 60 : 160 },
          children: [...time, kit.run(first), ...(rest.length === 0 ? note : [])],
        }),
      );
      rest.forEach((text, i) => {
        const last = i === rest.length - 1;
        children.push(
          kit.paragraph([kit.run(text), ...(last ? note : [])], { indent: LIST_INDENT, spacingAfter: last ? 160 : 60 }),
        );
      });
    } else {
      children.push(
        new docx.Paragraph({
          numbering: { reference: NUMBERING_FINDINGS, level: 0 },
          spacing: { after: 160 },
          children: [...time, kit.placeholder(placeholderFor(entry.body) ?? '')],
        }),
      );
    }
  }

  const notes = textParagraphs(doc.meta.opombe);
  if (notes.length > 0) {
    children.push(kit.heading('Opombe', 2), ...notes.map((text) => kit.paragraph([kit.run(text)])));
  }

  // Signature block: kept together on one page.
  children.push(
    new docx.Paragraph({ spacing: { before: 480, after: 0 }, keepNext: true, children: [] }),
    signatureLine(docx, kit, 'Kraj in datum'),
    signatureLine(docx, kit, 'Nadzornik', { value: textParagraphs(doc.meta.nadzornik).join(', ') }),
    signatureLine(docx, kit, 'Podpis', { last: true }),
  );

  return pack(docx, kit.document({ title: doc.title, description: 'Zapisnik nadzora', children }));
}
